<script>

import moment from 'moment';
import Apps from '@/core/Apps';
import { http } from '@/system/http';
import { forEach, find, orderBy } from 'lodash';
import { BRANDS } from '@/system/helpers/constants';
import ClientJournalTag from './ClientJournalTag.vue';
import ClientJournalEntry from './ClientJournalEntry.vue';
import JournalFilters from '@/system/components/client/JournalFilters.vue';
import {escapeRegExp, objectToFormData} from '@/system/helpers';
import printJournalsMixin from '@/system/helpers/journalPrintToPdf';
import JournalForm from '@/system/components/global/JournalForm.vue';
import ClientJournalAdvancedPrint from 'Components/client/ClientJournalAdvancedPrint.vue';
import SecureAuthenticationGate from 'Components/global/SecureAuthenticationGate.vue';
import VideoModal from 'Components/global/VideoModal.vue';
import Events from '@/core/Events'
import ClientTabs from 'Components/client/ClientTabs'
import InfiniteLoading from '../global/InfiniteLoading.vue';
import { useJournalsStore } from '@/system/stores/useJournalsStore';
import { mapStores } from 'pinia';
import { JournalTypes } from '@/system/helpers/constants';
import { useClientMainTabsStore } from '@/system/components/client/store/clientMainTabsStore';
const swal = require('sweetalert');

export default {

    mixins: [printJournalsMixin],
    data() {
        return {
            loading: true,
            sort_order: this.sort_order_initial,
            journals: [],
            journalFilters: [],
            journal_files: [],
            journal_files_upload_in_progress: false,
            journal_files_upload_progress_percentage: 0,
            journal_files_loading: false,
            journal: {},
            journalTags: [],
            create_active: false,
            search: '',
            brands: BRANDS,
            showSummaryByTag: false,
            videoFileTypes: ['mp4', 'mov', 'ogg', 'webm'],
            mountedJournalsCount: 0,
            showAdvancedPrintModal: false,
            hasLoadedJournals: false,
            hasLoadedJournalTags: false,
            hasLoadedJournalFiles: false,
            journals_next_page_url: null,
            journals_current_page_url: null,
            maxEntriesToShow: 10,
            ClientTabs
        };
    },

    props: {
        main_journal: {
            required: true
        },
        clientOrGroup: {
            required: true,
            type: Object,
        },
        isGroupJournal: {
            required: true,
            type: Boolean,
        },
        sort_order_initial: {
            default: 'DESC'
        },
        booking_id: {
            required: false,
        },
        bookings: {
            required: false,
            type: Array,
        },
        openCreateNewJournalForm: {
            required: false,
            type: Boolean,
            default: false,
        },
        openJournalEditForm: {
            required: false,
            type: Boolean,
            default: false,
        },
        defaultJournalToEdit: {
            required: false,
            type: Object,
            default: () => ({}),
        },
        privateJournalsByDefault: {
            required: false,
            type: Boolean,
            default: false,
        },
        withPrintForm: {
            required: false,
            type: Boolean,
            default: true,
        },
        autosaveAsDraft: {
            required: false,
            type: Boolean,
            default: true,
        },
        sidebar: {
            required: false,
            type: Boolean,
            default: false,
        },
        showFiles: {
            required: false,
            type: Boolean,
            default: true,
        }
    },

    mounted() {
        if (!this.isClientPage) {
            this.fetchJournalsAndRelatedData()
        }
        this.$event_bus.$on(Events.Client.TabChanged, (tab) => {
            if (tab === ClientTabs.Journals) {
                this.fetchJournalsAndRelatedData()
            }
        });

        this.$event_bus.$on('journalCreated', journal => {
            this.$nextTick(() => {
                this.journals.unshift($.extend({}, journal));
            });

            this.getJournalTags(true);
        });

        this.$event_bus.$on('closeCreateNewJournal', () => {
            this.create_active = false;
        });

        this.$event_bus.$on('openCreateNewJournal', () => {
            this.create_active = true;
        });

        this.$event_bus.$on('journalLocked', (journalId, lockedAt) => {
            let journal = find(this.journals, {id: journalId});
            journal.locked = true;
            journal.locked_at = lockedAt;
        });

        this.$event_bus.$on('journalUnlocked', (journalId) => {
            let journal = find(this.journals, {id: journalId});
            journal.locked = false;
            journal.locked_at = null;
        });

        this.$event_bus.$on('journalUpdated', updatedJournal => {
            // Replace journal entry
            let index = this.journals.findIndex((journal) => journal.id === updatedJournal.id);
            this.journals.splice(index, 1, updatedJournal);

            this.getJournalTags(true);
        });

        this.$event_bus.$on('journalMinimizedToggled', (journalId, toggle) => {
            let journal = find(this.journals, {id: journalId});
            journal.minified = toggle;
        });

        this.$event_bus.$on('journalFavorited', (journal_id, mark_as_favorite) => {
            forEach(this.journals, function (journal) {
                journal.favorite = journal_id === journal.id && mark_as_favorite ? 1 : 0;
            });

            this.journals = this.journals.sort((a, b) => a.favorite - b.favorite);
        });

        this.$event_bus.$on('journalDeleted', journal_id => {
            this.journals = this.journals.filter(j => j.id !== journal_id);

            this.hasLoadedJournals = false;
            this.getJournalEntries();

            this.getJournalTags(true);
        });

        //Listen to drawing saved event
        $(document).on('drawing_saved', event => {
            if (this.showFiles) {
                this.getJournalFiles(true);
            }
        });

        if (this.is_surveys_app_active && this.$can('see-surveys') && !this.isGroupJournal) {
            this.$store.dispatch('surveyRequests/load', this.clientOrGroup.id);
        }

        window.client_id = this.client?.id || null;

        this.$event_bus.$on('requestScrollToJournalEntry', (journalId, delay) => {
            this.scrollToJournalEntry(journalId, delay);
        });
    },

    methods: {
        getDateFormatted(date) {
            return moment(date, "DD-MM-YYYY").locale(moment_locale).format('LL');
        },

        async handleFileUpload() {
			if (!this.showFiles) {
				return;
			}

            const files = $("#journal-file-input")[0].files || [];

            // double check that none of the files exceed the 10 MB limit
            for (const file of files) {
                if (file.size > (10 * 1024 * 1024)) {
                    swal(trans('global.error_title'), trans('client.journal_file_save_error'), "warning");
                    $("#journal-file-input").data('dropify').clearElement();
                    return;
                }
            }

            let fileSizeUploaded = {};
            const totalFileSize = Object.values(files).reduce((total, file) => total + file.size, 0);
            const totalFileSizeUploaded = () => Object.values(fileSizeUploaded).reduce((total, size) => total + size, 0);
            let requestConfig = (file) => ({
                onUploadProgress: (progressEvent) => {
                    fileSizeUploaded[file.name] = progressEvent.loaded;
                    this.journal_files_upload_progress_percentage = Math.round((totalFileSizeUploaded() * 100) / totalFileSize);
                }
            });
            this.journal_files_loading = true;
            this.journal_files_upload_in_progress = true;
            this.journal_files_upload_progress_percentage = 0;
            let stopUploading = false;

            for (const file of files) {
                if (stopUploading) break;

                await http.post(
                    '/app/journal-files',
                    objectToFormData({ clientOrGroupId: this.clientOrGroup.id, forGroup: this.isGroupJournal, files: [file] }),
                    requestConfig(file)
                )
                    .then(({ data }) => {
                        fileSizeUploaded[file.name] = file.size;
                    })
                    .catch(({ error, errorBag }) => {
                        stopUploading = true;
                        this.$setErrorBag(errorBag);
                    });
            }

            if (!stopUploading) {
                toastr.success(trans('client.journal_file_save_success'));
            }

            this.journal_files_loading = false;
            this.journal_files_upload_in_progress = false;
            this.journal_files_upload_progress_percentage = 100;
            $('#journal-file-input').data('dropify').clearElement();

            if (this.showFiles) {
                this.getJournalFiles(true);
            }
        },

        openUploadPrompt() {
			if (!this.showFiles) {
				return;
			}

            $(".dropify-journal").trigger('click');
        },

        getPrintJournalTitle() {
            let page_title = this.clientOrGroup.name;

            if (!this.isGroupJournal && this.client.address) {
                page_title = page_title + ', ' + this.client.address + " " + this.client.zip + " " + this.client.city;
            }

            if (!this.isGroupJournal && this.client.cpr) {
                page_title = page_title + ', ' + trans('clients.social_security') + ': ' + this.client.cpr;
            }

            return page_title;
        },

        deleteJournalFilePrompt(journal_file) {
            swal({
                title: trans('settings.delete_image_title'),
                text: trans('settings.delete_image'),
                showCancelButton: true,
                cancelButtonText: trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: false,
            }, () => {
                this.deleteJournalFile(journal_file);
            });
        },

        deleteJournalFile(journal_file) {
            http.post(`/app/journal-files/${journal_file.id}/destroy`)
                .then(({ data }) => {
                    toastr.success(data.success);

                    this.journal_files = this.journal_files.filter(file => file.id !== journal_file.id);

                    swal.close();
                })
                .catch(({ error, errorBag }) => {
                    swal.close();
                });
        },

        getJournalFiles(reload = false) {
            if (reload) {
                this.hasLoadedJournalFiles = false;
            }

            if (!this.hasLoadedJournalFiles) {
                this.journal_files_loading = true;

                this.hasLoadedJournalFiles = true;

                http.get('/app/journal-files', {
                    params: {
                        clientOrGroupId: this.clientOrGroup.id,
                        forGroup: this.isGroupJournal,
                    },
                })
                    .then(({ data }) => {
                        this.journal_files = data;

                        this.journal_files_loading = false;
                    })
                    .catch(({ error, errorBag }) => {
                        this.journal_files_loading = false;

                        this.hasLoadedJournalFiles = false;
                    });
            }
        },

        showMoreEntries() {
            this.maxEntriesToShow += 10;

            if (this.journals_next_page_url !== null) {
                this.getJournalEntries(true);
            }
        },

        getJournalEntries(isLoadMore = false) {
            if (! this.hasLoadedJournals || isLoadMore) {
                this.loading = !isLoadMore;

                this.hasLoadedJournals = true;

                const url = isLoadMore ? this.journals_next_page_url : '/app/journal' + (this.booking_id ? `?bookingId=${this.booking_id}` : '');

                http.get(url, {
                    params: { clientOrGroupId: this.clientOrGroup.id, forGroup: this.isGroupJournal, filters: this.journalFilters || [] },
                })
                    .then(({ data }) => {
                        /**
                         * Filter out journals that already exist in this.journals
                         */
                        const existingJournalIds = this.journals.map(journal => journal.id);
                        const newJournals = data.data.filter(journal => ! existingJournalIds.includes(journal.id));

                        this.journals = [...this.journals, ...newJournals];
                        this.journals_next_page_url = data.links.next;
                        this.loading = false;

                        if (!this.create_active && this.openCreateNewJournalForm) {
                            this.showJournalForm();
                        }

                        if (!this.create_active && this.openJournalEditForm) {
                            this.scrollToJournalEntry(this.defaultJournalToEdit.id, 100);
                        }
                    })
                    .catch(({ error, errorBag }) => {
                        this.loading = false;

                        this.hasLoadedJournals = false;
                    });
            }
        },

        getJournalTags(reload = false) {
            if (reload) {
                this.hasLoadedJournalTags = false;
            }

            if (this.is_aspit_user && !this.hasLoadedJournalTags) {
                this.loading = true;

                this.hasLoadedJournalTags = true;

                http.get('/app/journal-journal-tags', {
                    params: {
                        clientOrGroupId: this.clientOrGroup.id,
                        forGroup: this.isGroupJournal,
                    },
                })
                    .then(({ data }) => {
                        this.journalTags = data;

                        this.loading = false;
                    })
                    .catch(({ error, errorBag }) => {
                        this.loading = false;

                        this.hasLoadedJournalTags = false;
                    });
            }
        },

        showJournalForm() {
            this.create_active = true;
            this.$event_bus.$emit('createNewJournal');
        },

        createNewJournal() {
            if (this.create_active) {
                this.$event_bus.$emit('closeCreateNewJournal');
            } else {
                this.showJournalForm();
            }
        },

        sortJournal(order) {
            this.sort_order = order;

            http.post(`/app/settings/apps/journal/settings`, { sorting: order })
                .then(({ data }) => {})
                .catch(() => {});
        },

        detectMob() {
            const toMatch = [
                /Android/i,
                /webOS/i,
                /iPhone/i,
                /iPad/i,
                /iPod/i,
                /BlackBerry/i,
                /Windows Phone/i
            ];

            return toMatch.some((toMatchItem) => {
                return navigator.userAgent.match(toMatchItem);
            });
        },
        showSurvey(surveyRequest) {
            this.$event_bus.$emit('showSurveyRequestModal', surveyRequest);
        },

        openPdfPreview(file) {
            this.$event_bus.$emit('show-pdf-preview-modal', { name: file.display_name, url: file.url });
        },

        scrollToJournalEntry(newId, delay) {
            this.$nextTick(() => {
                const element = document.getElementById('journal-entry-' + newId);

                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, delay);
                }
            });
        },

        handleJournalMounted() {
            this.mountedJournalsCount++;
            if (this.mountedJournalsCount === this.filtered_journals.length) {
                this.$event_bus.$emit('clientJournalMounted');
            }
        },

        printAllJournalsAdvanced() {
            this.showAdvancedPrintModal = true
        },

        fetchJournalsAndRelatedData () {
            this.getJournalEntries();
            if (this.showFiles) {
                this.getJournalFiles();
            }
            this.getJournalTags();
            this.$store.dispatch('journals/loadJournalTags');
        },

        handleFiltersChanged (filters) {
            this.journalFilters = filters;
            this.maxEntriesToShow = 10;
            // reload from first page when filters change
            this.journals = [];
            this.journals_next_page_url = null;
            this.hasLoadedJournals = false;
            this.getJournalEntries();
        },
    },

    watch: {
        sort_order: {
            handler: function (val, oldVal) {
                this.sortJournal(val);
            }
        },

        loading: {
            handler: function (val, oldVal) {
                if (!val) {
                    this.$nextTick(function () {
                        $('[data-toggle="tooltip"]').tooltip();
                        //Init drawing functionality (old code)
                        if (typeof init_colorbox !== "undefined") {
                            init_colorbox();
                        }
                    });
                }
            }
        },

        journal_files_loading: {
            handler: function (val, oldVal) {
                if (!val) {
                    this.$nextTick(function () {
                        //Init drawing functionality (old code)
                        if (typeof init_colorbox !== "undefined") {
                            init_colorbox();
                        }
                    });
                }

            }
        },

        journals: function (val, oldVal) {
            if (val.length !== oldVal.length && val.length === 0) {
                return;
            }

            this.$nextTick(() => {

                //Tooltips
                $('[data-toggle="tooltip"]').tooltip();

                if (this.showFiles) {
                    //Fileupload
                    let dropify_elem = $('.dropify-journal').dropify({
                        messages: { default: trans('client.drag_drop_file') }
                    });

                    dropify_elem.on('change', (event, element) => {
                        $('#' + ClientTabs.Journals + ' .dropify-wrapper').hide();

                        if (!this.journal_files_upload_in_progress) {
                            this.handleFileUpload();
                        }
                    });

                    // Show the dropzone when dragging files (not folders or page
                    // elements). The dropzone is hidden after a timer to prevent
                    // flickering to occur as `dragleave` is fired constantly.
                    let dragTimer;

                    $(document).on('dragover', (e) => {
                        let dt = e.originalEvent.dataTransfer;

                        if (dt.types && (dt.types.indexOf ? dt.types.indexOf('Files') != -1 : dt.types.contains('Files'))) {
                            $('#' + ClientTabs.Journals + ' .dropify-wrapper').show();
                            window.clearTimeout(dragTimer);
                        }
                    });

                    $(document).on('dragleave', (e) => {
                        dragTimer = window.setTimeout(() => {
                            $('#' + ClientTabs.Journals + ' .dropify-wrapper').hide();
                        }, 25);
                    });
                }
            });
        },

        'clientMainTabsStore.isJournalTabActive': {
            handler (val) {
                if (!this.sidebar && val) {
                    this.fetchJournalsAndRelatedData();
                }
            },
            immediate: true,
        }
    },

    computed: {
        ...mapStores(useJournalsStore, useClientMainTabsStore),

        hasNonDraftJournals() {
            return this.journals.filter(journal => ! journal.is_draft).length > 0;
        },

        is_aspit_user() {
            const brand = this.$store.state.brand
            return brand === this.brands.Physica || brand === this.brands.Psykbase;
        },

        survey_requests() {
            return this.$store.getters['surveyRequests/all'];
        },

        answered_survey_requests() {
            return this.survey_requests.filter(
                (survey) => survey.answered && survey.survey.is_showed_in_journals
            )
        },

        is_surveys_app_active() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Surveys);
        },

        search_clean() {
            return (this.search || '');
        },

        search_regex() {
            return escapeRegExp(this.search_clean);
        },

        is_searching() {
            return this.search_clean.length > 0;
        },

        has_journals_to_display() {
            return this.journals.length > 0
                || (this.is_surveys_app_active && this.answered_survey_requests.length > 0);
        },

        hasAppliedFilters() {
            return (this.journalFilters || []).length > 0;
        },

        hasAnyJournalOrFileContent() {
            return this.has_journals_to_display || (this.showFiles && this.journal_files.length > 0);
        },

        should_show_create_first_journal_entry() {
            return !this.is_searching
                && !this.hasAppliedFilters
                && !this.hasAnyJournalOrFileContent
                && !(this.showFiles && this.journal_files_loading)
                && !(this.is_surveys_app_active && this.answered_survey_requests.length > 0);
        },

        should_show_journal_controls() {
            return this.hasAnyJournalOrFileContent || this.is_searching || this.hasAppliedFilters;
        },

        filtered_journals() {
            let filtered_journals = (this.journals || []).map(journal => ({
                ...journal,
                is_survey: false,
            }));

            const journalType = this.journalsFiltersStore.journalType;

            // If journal_type is explicitly 'survey', do not include any journal rows
            if (journalType === JournalTypes.Survey) {
                filtered_journals = [];
            }

            // Include surveys only when no journal_type is selected, or when it is 'survey'
            if (this.is_surveys_app_active && (!journalType || journalType === JournalTypes.Survey)) {
                let survey_requests = this.journalsFiltersStore.filterSurveyResponses(this.answered_survey_requests);

                survey_requests = forEach(survey_requests, function (survey) {
                    const date_formatted = moment(survey.answered_at, "YYYY-MM-DD HH:mm:ss");

                    survey.date = date_formatted.format("DD-MM-YYYY");
                    survey.is_survey = true;
                    survey.favorite = false;
                    survey.content = "";
                });

                filtered_journals = filtered_journals.concat(survey_requests);
            }

            if (this.is_searching) {
                filtered_journals = filtered_journals.filter(journal => {
                    let strippedContent = journal.content.replace(/(<([^>]+)>)/gi, "");   // ignore any HTML elements in the search

                    return (new RegExp(this.search_regex, 'gi')).test(strippedContent);
                }).map(journal => {
                    let reg;
                    if (journal.content[0] === '<') {
                        // this helps us to highlight only tag contents, without highlighting tag names/attributes
                        reg = new RegExp(`>[^<]*(${this.search_regex})[^<]*<`, 'gi');
                    } else {
                        reg = new RegExp(`(${this.search_regex})`, 'gi');
                    }

                    return {
                        ...journal,
                        content: (journal.content || '').replace(
                            reg,
                            (match, group) => {
                                return match.replace(new RegExp(this.search_regex, 'gi'), `<span class="highlight">${group}</span>`, match);
                            }
                        )
                    }
                })
            }

            const sort_order = this.sort_order.toLowerCase();

            filtered_journals.sort((a, b) => {
                if (Number(a.favorite) - Number(b.favorite) !== 0) {
                    return Number(b.favorite) - Number(a.favorite);
                }

                const aFormattedDate = moment(a.date, 'DD-MM-YYYY').format('YYYY-MM-DD');
                const bFormattedDate = moment(b.date, 'DD-MM-YYYY').format('YYYY-MM-DD');

                if (sort_order === 'asc') {
                    return aFormattedDate !== bFormattedDate
                        ? aFormattedDate.localeCompare(bFormattedDate)
                        : a.id - b.id;
                }

                return aFormattedDate !== bFormattedDate
                    ? bFormattedDate.localeCompare(aFormattedDate)
                    : b.id - a.id;
            });

            return filtered_journals.slice(0, this.maxEntriesToShow);
        },

        filtered_journal_tags() {
            let filteredJournalTags = []

            if (this.journalTags) {
                Object.entries(this.journalTags).forEach(function(tagData, index) {
                    filteredJournalTags.push({
                        'id': tagData[0],
                        'color': tagData[1][0].color,
                        'name': tagData[1][0].name,
                        'content': tagData[1].map(tag => {
                            return tag.content
                        }),
                    })
                })
            }

            return filteredJournalTags
        },

        has_favorite() {
            return (this.journals || []).some(journal => journal.favorite);
        },

        show_sort_dropdown() {
            return (this.journals || []).length >= 2;
        },

        filtered_journal_files() {
            let journalFiles = this.journal_files.map(file => ({
                ...file,
                title: file.display_name,
            }));

            if (this.is_searching) {
                journalFiles = journalFiles.filter(file => {
                    return (new RegExp(this.search_regex, 'gi')).test(file.display_name)
                }).map(file => ({
                    ...file,
                    display_name: (file.display_name || '').replace(
                        new RegExp(this.search_regex, 'gi'),
                        match => `<span class="highlight">${match}</span>`
                    )
                }));
            }

            return orderBy(journalFiles, ['id'], [this.sort_order.toLowerCase()]);
        },

        is_usermanagement_enabled() {
            return this.$store.getters['apps/isAppEnabled'](Apps.UserManagement)
                || this.$store.getters['apps/isAppEnabled'](Apps.Tenants);
        },

        is_owner() {
            return this.$store.getters['auth/isOwner'];
        },

        can_edit_client() {
            return this.$can('edit', this.isGroupJournal ? 'clientGroup' : 'client', this.clientOrGroup);
        },
        can_see_client() {
            return this.$can('see', this.isGroupJournal ? 'clientGroup' : 'client', this.clientOrGroup);
        },
        can_edit_journal() {
            return (this.can_see_client || this.can_edit_client) && this.$can('edit', 'journals');
        },
        is_treatments_active() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Treatments);
        },
        isClientPage() {
            return this.$route.path.includes('/app/client/')
                || window.location.pathname.includes('/app/client/');
        },

        isNewJournalExperienceEnabled() {
            return this.$store.state.featureFlags.new_journal_experience;
        },
    },

    components: {
        ClientJournalAdvancedPrint,
        JournalFilters,
        ClientJournalEntry,
        ClientJournalTag,
        JournalForm,
        SecureAuthenticationGate,
        InfiniteLoading,
        VideoModal,
    },
};
</script>

<template id="client-journal">
    <div
        class="tab-inner"
        style="min-height: auto"
        :class="{
            'tab-gradient': !loading && (showFiles ? !journal_files_loading && (has_journals_to_display || journal_files.length > 0) : has_journals_to_display),
        }"
        :id="ClientTabs.Journals"
    >
        <!-- This form is used to consistently print all journal entries -->
        <form v-if="withPrintForm" name="journalPrintForm" style="display: none;" method="get" target="print-iframe">
            <input type="text" name="orientation" value="portrait">
            <input type="text" name="treatment" value="">
            <input type="text" name="filters" value="">
        </form>

        <secure-authentication-gate :app-name="trans('global.journals')">
            <div class="journal">
                <div class="row" v-show="loading">
                    <div class="col-sm-12 text-center">
                        <div class="preloader"></div>
                    </div>
                </div>

                <div class="journal-inner" v-show="!loading">
                    <form v-if="showFiles" id="journal-file-form" enctype="multipart/form-data" method="post" role="form" action="">
                        <input type="file"
                               class="dropify-journal"
                               id="journal-file-input"
                               name="files[]"
                               style="opacity:0;"
                               multiple
                        />
                    </form>

                    <div class="create-first-journal-entry"
                         v-if="should_show_create_first_journal_entry"
                         v-show="!create_active"
                    >
                        <h2 class="text-center m-b-30 m-t-30">{{ trans('client.journal_no_entries') }}</h2>

                        <div class="row text-center m-b-30" v-if="can_edit_journal">
                            <div class="col-md-1 col-lg-2"></div>

                            <div
                                class="col-xs-12"
                                :class="{'col-md-4 col-lg-3': showFiles, 'col-md-12 col-lg-12': !showFiles}"
                            >
                                <a href="" @click.prevent="createNewJournal" class="btn btn-custom btn-lg" >
                                    <i class="fa-regular fa-plus m-r-5"></i> {{ trans('client.journal_create') }}
                                </a>
                            </div>

                            <div v-if="showFiles" class="col-xs-12 col-md-2 col-lg-2">
                                <h4 class="text-muted">{{ trans('global.or') }}</h4>
                            </div>

                            <div v-if="showFiles" class="col-xs-12 col-md-4 col-lg-3">
                                <a href="#" v-on:click.prevent="openUploadPrompt()" class="btn btn-custom btn-lg">
                                    <i class="fa-solid fa-file-plus m-r-5"></i> {{ trans('client.create_your_first_file') }}
                                </a>
                            </div>

                            <div class="col-md-1 col-lg-2"></div>
                        </div>
                    </div>

                    <div class="journal-controls"
                         style="display:block !important;"
                         v-if="should_show_journal_controls"
                    >
                        <a :title="trans('client.journal_create')"
                           class="btn btn-custom create-journal-btn m-r-5"
                           @click.prevent="createNewJournal()"
                           v-if="can_edit_journal && !showSummaryByTag"
                        >
                            <i class="fa-regular fa-plus" v-if="!create_active"></i>
                            <i class="fa-regular fa-minus" v-if="create_active"></i>
                            <span class="hidden-xs m-l-5">{{ trans('client.journal_create') }}</span>
                        </a>

                        <a :title="trans('client.add_file_or_image')"
                           @click.prevent="openUploadPrompt()"
                           class="btn btn-custom btn-trans t m-r-5"
                           href=""
                           v-if="can_edit_journal && !showSummaryByTag && showFiles"
                        >
                            <i class="fa-solid fa-file-plus"></i>
                            <span class="hidden xl:inline-block m-l-5">{{ trans('client.add_file_or_image') }}</span>
                        </a>

                        <a :title="showSummaryByTag ? trans('client.client_journals') : trans('client.summary_by_tag')"
                           @click.prevent="showSummaryByTag = showSummaryByTag ? false : true"
                           class="btn btn-custom btn-trans t m-r-5"
                           href=""
                           v-if="can_edit_journal && is_aspit_user"
                        >
                            <i class="fa-solid" :class="{'fa-book': showSummaryByTag, 'fa-tag': !showSummaryByTag}"></i>
                            <span class="hidden xl:inline-block m-l-5">{{ showSummaryByTag ? trans('client.client_journals') : trans('client.summary_by_tag') }}</span>
                        </a>

                        <a :title="trans('client.journal_download')"
                           class="hidden-xs btn btn-custom btn-trans m-r-5"
                           :href="`/app/download-all-journals?clientOrGroupId=${clientOrGroup.id}&forGroup=${isGroupJournal}`"
                           download
                           v-if="!showSummaryByTag"
                        >
                            <i class="zmdi zmdi-cloud-download"></i>
                            <span class="hidden-xs hidden-sm hidden-md hidden-lg  m-l-5">{{ trans('client.journal_download') }}</span>
                        </a>


                        <span class="dropdown" v-if="!showSummaryByTag">
                        <a
                            href="#"
                            data-toggle="dropdown"
                            :title="trans('client.journal_print')"
                            class="btn btn-custom btn-trans m-r-5 dropdown-toggle"
                        >
                            <i class="zmdi zmdi-print"></i>
                            <span class="hidden-xs hidden-sm hidden-md hidden-lg  m-l-5">{{ trans('client.journal_print') }}</span>
                        </a>
                        <ul class="dropdown-menu">
                            <li><a href="#" @click.prevent="printAllJournals('portrait', journalFilters)"><span class="text-base">{{ trans('client.portrait') }}</span></a></li>
                            <li><a href="#" @click.prevent="printAllJournals('landscape', journalFilters)"><span class="text-base">{{ trans('client.landscape') }}</span></a></li>
                            <template v-if="is_treatments_active">
                                <hr class="my-0">
                                <li>
                                    <a href="#" @click.prevent="printAllJournalsAdvanced">
                                        <span class="text-base">{{ trans('apps.treatments.client_print_journal_advanced') }}</span>
                                    </a>
                                </li>
                            </template>
                        </ul>
                    </span>

                        <a v-if="is_usermanagement_enabled && is_owner && main_journal && !showSummaryByTag"
                           href=""
                           v-tooltip
                           :title="trans('client.journal_log_tooltip')"
                           @click.prevent="$event_bus.$emit('viewJournalLog', clientOrGroup, isGroupJournal)"
                           class="hidden-xs btn btn-custom btn-trans m-r-10"
                        >
                            <i class="zmdi zmdi-eye"> </i>
                        </a>

                        <div class="w-full mt-6 inline-flex" v-if="!showSummaryByTag">
                            <select v-model="sort_order" class="hidden-xs form-control sort-journal-entries sm:mr-3" v-if="show_sort_dropdown">
                                <option value="DESC">{{ trans('client.journal_sort_new') }}</option>
                                <option value="ASC">{{ trans('client.journal_sort_old') }}</option>
                            </select>

                            <div class="w-full sm:w-16 relative">
                                <input type="text"
                                       class="form-control"
                                       name="search"
                                       v-model="search"
                                       :placeholder="`${trans('app.search')}...`"
                                />
                                <span v-if="(search || '').length > 0"
                                      @click.prevent="search = ''"
                                      class="absolute top-0 bottom-0 right-0 pr-5 cursor-pointer flex items-center"
                                ><i class="zmdi zmdi-close-circle"></i></span>
                            </div>
                        </div>
                    </div>

                    <!-- CREATE CLIENT JOURNAL SLIDE IN FORM -->
                    <div class="client-journal-form-slide m-t-10"
                         :class="{ 'card-box bg-box' : filtered_journals.length > 0 }"
                         v-if="can_edit_journal"
                         v-show="create_active"
                    >
                        <journal-form
                            :client-or-group="clientOrGroup"
                            :isGroupJournal="isGroupJournal"
                            :booking_id="booking_id"
                            :bookings="bookings"
                            :has-previous-journal-entries="hasNonDraftJournals"
                            :is-private-by-default="privateJournalsByDefault"
                            :autosave-as-draft="autosaveAsDraft"
                        />
                    </div>

                    <!-- Filters -->
                    <journal-filters
                        v-if="(has_journals_to_display || hasAppliedFilters) && isNewJournalExperienceEnabled"
                        @filters-changed="handleFiltersChanged"
                    />

                    <div class="row">
                        <div :class="{ 'col-xs-12 no-journal-entries' : !has_journals_to_display, 'col-md-12 col-lg-3 col-lg-push-9' : has_journals_to_display || filtered_journals.length > 0}"
                             v-if="showFiles && (journal_files.length > 0 || journal_files_loading)"
                             style="position:relative; min-height:170px;"
                        >
                            <div class="text-center journal-files-loading" v-if="journal_files_loading">
                                <div class="preloader"></div>
                                <div style="max-width: 200px; margin-left: auto; margin-right: auto; margin-top:-25px;">
                                    <progress-bar v-if="journal_files_upload_in_progress"
                                                  :max="100"
                                                  :current="journal_files_upload_progress_percentage"
                                    ></progress-bar>
                                </div>
                            </div>

                            <div class="journal_files_inner clearfix">
                                <div class="journal-file card-box bg-box" v-for="journal_file in filtered_journal_files" :key="journal_file.id">
                                    <div class="journal-file-information">
                                        <ul class="pull-right nav nav-pills ">
                                            <li>
                                                <a :href="journal_file.download_url" v-tooltip="trans('global.download')">
                                                    <i class="zmdi zmdi-download"></i>
                                                </a>
                                            </li>
                                            <li v-if="can_edit_journal">
                                                <a v-on:click.prevent="deleteJournalFilePrompt(journal_file)"
                                                   href="#"
                                                   v-tooltip="trans('global.delete')"
                                                >
                                                    <i class="fa fa-trash"></i>
                                                </a>
                                            </li>
                                        </ul>

                                        <h5 :class="{'truncate': !is_searching}"
                                            v-tooltip
                                            :key="`filename-tooltip-for-${journal_file.id}`"
                                            :title="journal_file.title"
                                        >
                                            {{ journal_file.display_name }}
                                        </h5>
                                    </div>

                                    <div class="journal-file-thumbnail">
                                        <!-- image -->
                                        <a target="_blank"
                                           :href="journal_file.url"
                                           :data-width='journal_file.image_width'
                                           :data-type='journal_file.file_type'
                                           :data-height='journal_file.image_height'
                                           :data-id='journal_file.id'
                                           :data-drawing='journal_file.has_drawing'
                                           :data-name='journal_file.display_name'
                                           class="popupable"
                                           style="position:relative;display:block;"
                                           v-if="journal_file.type === 'image'"
                                        >
                                            <img :src="journal_file.thumb_url" class="thumb-img" >
                                            <div class="drawing-wrap">
                                                <img v-if='journal_file.has_drawing'
                                                     :src="journal_file.drawing"
                                                     alt="drawing"
                                                     class="drawing-overlay"
                                                />
                                            </div>
                                        </a>

                                        <!-- video file -->
                                        <a :href="journal_file.url"
                                           class="block video-popup"
                                           data-type="file"
                                           :data-name="journal_file.display_name"
                                           v-if="journal_file.type === 'file' && videoFileTypes.includes(journal_file.file_type)"
                                        >
                                            <img :src="journal_file.thumb_url" width="100%" />
                                        </a>

                                        <!-- non pdf file -->
                                        <i class="zmdi zmdi-file" v-else-if="journal_file.type === 'file' && journal_file.file_type !== 'pdf'"></i>

                                        <!-- pdf file -->
                                        <a v-else-if="journal_file.file_type === 'pdf' && !detectMob()" @click.prevent.stop="openPdfPreview(journal_file)">
                                            <i class="zmdi zmdi-file"></i>
                                        </a>

                                        <a v-else-if="journal_file.file_type === 'pdf' && detectMob()" target="_blank" :href="journal_file.url">
                                            <i class="zmdi zmdi-file"></i>
                                        </a>
                                    </div>

                                    <div v-if="journal_file.created_at" class="m-t-5 m-b-0">
                                        <small class="text-muted">{{ trans("client.journal_image_uploaded_at", {date : journal_file.date_formatted}) }}</small>
                                    </div>
                                </div>

                                <h3 v-if="is_searching && filtered_journal_files.length === 0" class="text-center m-b-30 mt-10">{{ trans('client.file_search_empty') }}</h3>
                            </div>
                        </div>

                        <div v-if="!showSummaryByTag" :class="{'col-xs-12' : !showFiles || journal_files.length === 0, 'col-md-12 col-lg-9 col-lg-pull-3' : showFiles && (journal_files.length > 0 || journal_files_loading) }">
                            <div class="journal-entries"
                                 :class="{ 'empty-entries' : filtered_journals.length === 0}"
                            >
                                <infinite-loading
                                    v-if="filtered_journals.length > 0"
                                    :is-dropdown="false"
                                    :has-finished-loading="journals_next_page_url !== null || maxEntriesToShow < journals.length + answered_survey_requests.length"
                                    @load-more="showMoreEntries"
                                >
                                    <client-journal-entry
                                        v-for="journal in filtered_journals" :id="'journal-entry-' + journal.id"
                                        :key="journal.id"
                                        :client-or-group="clientOrGroup"
                                        :bookings="bookings"
                                        :initial-journal="journal"
                                        :open-journal-edit-form="openJournalEditForm && defaultJournalToEdit && defaultJournalToEdit.id === journal.id"
                                        @journal-mounted="handleJournalMounted"
                                    />
                                </infinite-loading>

                                <h2 v-if="(is_searching || hasAppliedFilters) && filtered_journals.length === 0" class="text-center m-b-30 mt-12">{{ trans('client.journal_search_empty') }}</h2>
                            </div>
                        </div>

                        <div v-else class="col-xs-12">
                            <div class="journal-entries"
                                 :class="{ 'empty-entries' : filtered_journal_tags.length === 0}"
                            >
                                <client-journal-tag v-for="tag in filtered_journal_tags" :key="tag.id" :journal-tag="tag"></client-journal-tag>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </secure-authentication-gate>

        <client-journal-advanced-print
            v-if="showAdvancedPrintModal"
            :client-or-group="clientOrGroup"
            :sidebar="sidebar"
            :filters="journalFilters"
            @printed="showAdvancedPrintModal = false"
        >
        </client-journal-advanced-print>

        <video-modal></video-modal>
    </div>
</template>
